import React from "react";
import { Cpu, CircuitBoard, Code } from "lucide-react";
import SiteShell from "../../components/SiteShell";
import SEO from "../../components/SEO";
import { DeptHero, DeptSection, PersonCard, CardGrid } from "./_shared";

/**
 * /team/rnd — Electrical & Electronic R&D.
 *
 * Descriptions ladder each engineer's work to a specific piece of the
 * GREEN + GRID stack (BMS hardware, firmware, griddata edge, board
 * design, or the pilot factory line).
 */

const embeddedTeam = [
  {
    name: "Balaji",
    role: "Embedded Hardware Engineer",
    image: "/images/team/balaji.png",
    description:
      "GREEN + GRID: builds the hardware inside every BESS pack — sensor front-ends and comms buses feeding griddata's dispatch layer.",
  },
  {
    name: "Abhishek",
    role: "Embedded Hardware Engineer",
    image: "/images/team/abhishek.jpg",
    description:
      "Designs and validates BMS-adjacent hardware — the interface between LFP cells, the thermal loop, and the pack-level control stack.",
  },
  {
    name: "Nandha Kumar",
    role: "Embedded Software Engineer",
    image: "/images/team/nandha-kumar.png",
    description:
      "Writes the firmware that estimates state-of-charge and cell balancing in real time — a Made-in-India replacement for imported BMS stacks.",
  },
  {
    name: "Karthik RR",
    role: "Embedded Software Engineer",
    image: "/images/team/karthik-rr.png",
    description:
      "Owns the on-device control loop — protocol layers that push telemetry from every deployed pack up into the griddata screen.",
  },
];

const designTeam = [
  {
    name: "Sivasamy",
    role: "PCB Route Planner & Designer",
    image: "/images/team/sivasamy.png",
    description:
      "Routes the multi-layer boards inside every BMS and inverter interface — signal integrity for cells that must last a 20-year cycle life.",
  },
  {
    name: "Sathish Kumar",
    role: "Hardware Schematic Design & PCB Library Creation",
    image: "/images/team/sathish-kumar.png",
    description:
      "Curates the schematic library the entire product suite draws from — one canonical part list across BESS, chargers, and IoT SKUs.",
  },
  {
    name: "Santhana Bharathi",
    role: "Embedded Hardware Design & PCB Design",
    image: "/images/team/santhana-bharathi.png",
    description:
      "Bridges schematic-to-layout for the pack electronics — DFM-ready boards that walk straight onto the SIPCOT SMT line.",
  },
  {
    name: "Karthikeyan",
    role: "PCB Design & Routing",
    image: "/images/team/karthikeyan.png",
    description:
      "Runs multi-layer high-speed routing for the griddata edge gateways sitting on every substation and cooperative panel.",
  },
  {
    name: "Rajeshwaran",
    role: "IC Package Design and Schematic Design",
    image: "/images/team/rajeshwaran.png",
    description:
      "Chip-package integration for the BMS ASIC path — a non-Chinese semiconductor lane across the Indo-Pacific supply chain.",
  },
  {
    name: "Saravaprakash",
    role: "3D Design and Product Development Engineer",
    image: "/images/team/saravaprakash.png",
    description:
      "Turns pack, charger, and gateway drawings into manufacturable enclosures — 45 °C ambient tolerant, monsoon salt-spray rated.",
  },
];

const softwareTeam = [
  {
    name: "Mohana Priya",
    role: "Lead Hardware Assembly Line and Testing",
    image: "/images/team/mohana-priya.png",
    description:
      "Runs the SMT-and-final-test line inside the 720 MWh factory — every board leaves with a serialised cycle-and-EIS record.",
  },
  {
    name: "Priyanka Anand",
    role: "Mobile Apps Developer",
    image: "/images/team/priyanka-anand.png",
    description:
      "Ships the griddata mobile surface — the pane where a cooperative member sees their share of generation, dispatch, and dividend.",
  },
  {
    name: "Sathya Natrajan",
    role: "Full Stack Developer",
    image: "/images/team/sathya-natrajan.png",
    description:
      "Owns the griddata backend — Indian tariff engine, GCPP settlement math, and the digital twin that mirrors every deployed pack.",
  },
  {
    name: "Nishant Kumar",
    role: "Research Scholar",
    image: "/images/team/nishant-kumar.png",
    description:
      "Prototypes what ships in two years — sodium-ion chemistry variants and next-gen dispatch algorithms feeding into the roadmap.",
  },
];

export default function RnD() {
  return (
    <SiteShell>
      <SEO
        title="R&D Team — Green Gold Energy"
        description="The IIT bench and engineers behind the cell-to-pack-to-grid stack — hardware, firmware, and the griddata edge."
      />
      <DeptHero
        kicker="Research & Development"
        title="Engineers and IIT professors"
        sub="The IIT bench and engineers turning coconut shells, sodium-ion cathodes, and hard-carbon anodes into the next generation of Indian batteries — and the firmware, boards, and enclosures that carry them into the field."
        Icon={Cpu}
      />

      <DeptSection
        Icon={Cpu}
        title="Embedded Systems"
        description="Firmware and hardware engineers building the intelligence inside every deployed pack."
      >
        <CardGrid minCol={360}>
          {embeddedTeam.map((m) => (
            <PersonCard key={m.name} member={m} />
          ))}
        </CardGrid>
      </DeptSection>

      <DeptSection
        Icon={CircuitBoard}
        title="Design & PCB"
        description="Schematics, layout, routing and IC-package design — turning circuit ideas into manufacturable boards."
      >
        <CardGrid minCol={360}>
          {designTeam.map((m) => (
            <PersonCard key={m.name} member={m} />
          ))}
        </CardGrid>
      </DeptSection>

      <DeptSection
        Icon={Code}
        title="Software & Testing"
        description="Assembly line, mobile, full-stack and research work that closes the loop between hardware and the user."
      >
        <CardGrid minCol={360}>
          {softwareTeam.map((m) => (
            <PersonCard key={m.name} member={m} />
          ))}
        </CardGrid>
      </DeptSection>

      <div style={{ height: 60 }} />
    </SiteShell>
  );
}
